import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Caption, ThemedText } from '@/components/ui/Text';
import { AppImage } from '@/components/ui/AppImage';
import type { ImageSourcePropType } from 'react-native';
import type { TripSummary, TripSummaryMoment } from '@/services/tripSummary';
import type { ItineraryItem } from '@/services/itinerary';

type TripFullRecapShareCardProps = {
  summary: TripSummary;
  landmarkImageUrl?: string | null;
  fallbackImageSource: ImageSourcePropType;
  mapSnapshotUrl?: string | null;
};

const StatPill = ({ label }: { label: string }) => (
  <View
    style={{
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 999,
      backgroundColor: '#1F2937',
      borderWidth: 1,
      borderColor: '#374151',
    }}
  >
    <Caption style={{ color: '#F9FAFB', fontSize: 16, lineHeight: 22, fontWeight: '700' }}>{label}</Caption>
  </View>
);

const SectionTitle = ({ children }: { children: string }) => (
  <ThemedText
    variant="bodyStrong"
    style={{ color: '#F9FAFB', fontSize: 26, lineHeight: 34, fontWeight: '700', includeFontPadding: false }}
  >
    {children}
  </ThemedText>
);

const getCategoryIcon = (category: ItineraryItem['category']): keyof typeof Ionicons.glyphMap => {
  switch (category) {
    case 'flight':
      return 'airplane-outline';
    case 'hotel':
      return 'bed-outline';
    case 'activity':
      return 'ticket-outline';
    case 'ferry':
      return 'boat-outline';
    case 'train':
      return 'train-outline';
    case 'transportation':
      return 'bus-outline';
    default:
      return 'location-outline';
  }
};

const MomentCard = ({ moment }: { moment: TripSummaryMoment }) => (
  <View
    style={{
      flex: 1,
      minWidth: 0,
      padding: 14,
      borderRadius: 18,
      backgroundColor: '#111827',
      borderWidth: 1,
      borderColor: '#374151',
      gap: 6,
    }}
  >
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 999,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(96, 165, 250, 0.18)',
      }}
    >
      <Ionicons name={getCategoryIcon(moment.category)} size={16} color="#60A5FA" />
    </View>
    <ThemedText
      numberOfLines={2}
      variant="bodyStrong"
      style={{ color: '#F9FAFB', fontSize: 18, lineHeight: 22, includeFontPadding: false }}
    >
      {moment.title}
    </ThemedText>
    {moment.location ? (
      <Caption numberOfLines={1} style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 18 }}>
        {moment.location}
      </Caption>
    ) : null}
    <Caption style={{ color: '#D1D5DB', fontSize: 12, lineHeight: 16, fontWeight: '700' }}>
      {moment.dateLabel}
    </Caption>
  </View>
);

export function TripFullRecapShareCard({
  summary,
  landmarkImageUrl,
  fallbackImageSource,
  mapSnapshotUrl,
}: TripFullRecapShareCardProps) {
  const heroSource = landmarkImageUrl ? { uri: landmarkImageUrl } : fallbackImageSource;
  const moments = summary.topMoments.slice(0, 3);
  const statLabels = [
    `${summary.metrics.durationDays} days`,
    `${summary.metrics.placesVisitedCount} places`,
    `${summary.collaboratorCount || 1} travelers`,
    `${summary.checklist.completed}/${summary.checklist.total || 0} checklist`,
  ];

  return (
    <View
      style={{
        width: 1080,
        height: 1920,
        backgroundColor: '#0B1220',
        justifyContent: 'space-between',
      }}
    >
      <View style={{ height: 320, position: 'relative' }}>
        <AppImage source={heroSource} resizeMode="cover" style={{ width: '100%', height: '100%' }} />
        <LinearGradient
          colors={['rgba(11, 18, 32, 0.1)', 'rgba(11, 18, 32, 0.72)', '#0B1220']}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <View style={{ position: 'absolute', left: 48, right: 48, bottom: 24, gap: 8 }}>
          <Caption style={{ color: '#E5E7EB', fontSize: 16, lineHeight: 22, letterSpacing: 1.6, fontWeight: '700' }}>
            TRIP RECAP
          </Caption>
          <ThemedText
            numberOfLines={2}
            variant="title"
            style={{ color: '#FFFFFF', fontSize: 40, lineHeight: 46, fontWeight: '800', includeFontPadding: false }}
          >
            {summary.metrics.tripTitle}
          </ThemedText>
        </View>
      </View>

      <View style={{ paddingHorizontal: 48, gap: 22 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {statLabels.map((label) => (
            <StatPill key={label} label={label} />
          ))}
        </View>

        {mapSnapshotUrl ? (
          <View style={{ gap: 10 }}>
            <SectionTitle>Places visited</SectionTitle>
            <View
              style={{
                height: 360,
                borderRadius: 22,
                overflow: 'hidden',
                borderWidth: 1,
                borderColor: '#374151',
                backgroundColor: '#111827',
              }}
            >
              <AppImage
                source={{ uri: mapSnapshotUrl }}
                resizeMode="cover"
                style={{ width: '100%', height: '100%' }}
              />
            </View>
            <Caption style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 20 }}>
              {`Showing ${Math.min(summary.mappedPlaces.length, 12)} pinned stops from your itinerary`}
            </Caption>
          </View>
        ) : null}

        {moments.length >= 2 ? (
          <View style={{ gap: 10 }}>
            <SectionTitle>Top moments</SectionTitle>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              {moments.map((moment) => (
                <MomentCard key={moment.id} moment={moment} />
              ))}
            </View>
          </View>
        ) : null}
      </View>

      <View style={{ paddingTop: 16, paddingBottom: 48, alignItems: 'center' }}>
        <Caption
          style={{
            color: '#9CA3AF',
            textAlign: 'center',
            fontSize: 20,
            lineHeight: 28,
            fontWeight: '700',
            includeFontPadding: false,
          }}
        >
          Powered by Tripvio
        </Caption>
      </View>
    </View>
  );
}
